import React from 'react';
import {Navigate, Outlet} from 'react-router-dom';
import {getAuthenticatedUserType, isTokenValid} from "./utils/Utils";
import * as LinkFactory from "./routing/LinkFactory";

type AllowedProp = string | string[] | null | undefined;

/**
 * ProtectedRoute
 * - When `allowed` is provided: verifies a valid JWT exists AND the user's type
 *   (derived from the token, not a tamperable cookie) matches the allowed list.
 * - When `requireAuth` is true (without `allowed`): verifies a valid JWT exists
 *   but does not restrict by role — any authenticated user can access.
 * - When neither is provided: renders children without auth check (public routes).
 * - Redirects to login when token is missing/expired.
 * - Redirects to unauthorized when user type doesn't match.
 */
const ProtectedRouteComponent: React.FC<{ children?: React.ReactNode; allowed?: AllowedProp; requireAuth?: boolean }> = ({children, allowed, requireAuth}) => {
    const allowedNormalized = React.useMemo<string[] | null>(() => {
        if (allowed == null) return null;
        return typeof allowed === 'string' ? [allowed] : Array.isArray(allowed) ? allowed : null;
    }, [allowed]);

    // Public routes (no `allowed` prop and no `requireAuth`) — no auth check required
    if (allowedNormalized == null && !requireAuth) return children ? <>{children}</> : <Outlet/>;

    // All protected routes — require a valid, non-expired JWT
    const tokenValid = isTokenValid();
    if (!tokenValid) {
        return <Navigate to={LinkFactory.login} replace/>;
    }

    // If only requireAuth (no role restriction), allow any authenticated user
    if (allowedNormalized == null) return children ? <>{children}</> : <Outlet/>;

    // Derive user type from JWT payload (not from a tamperable cookie)
    const currentUserType = getAuthenticatedUserType() ?? '';
    const isAllowed = allowedNormalized.includes(currentUserType);

    if (!isAllowed) {
        return <Navigate to={LinkFactory.unauthorized_route} replace/>;
    }

    return children ? <>{children}</> : <Outlet/>;
};
const ProtectedRoute = React.memo(ProtectedRouteComponent);
export default ProtectedRoute;